import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { FROM_ADMIN } from '../../nav.ts';
import { formatPeriod } from '../../../lib/format/dates.ts';
import { ApiFailure, api, type ScheduleSummary } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy } from '../../copy/en-CA.ts';

const c = copy.admin.schedules;

export function AdminSchedulesPage() {
  const [rows, setRows] = useState<ScheduleSummary[] | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.schedules();
      setRows(r.schedules);
      setCurrentId(r.currentId);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (s: ScheduleSummary) => {
    const ok = window.confirm(s.status === 'draft' ? c.confirmDiscard(s.name) : c.confirmDelete(s.name));
    if (!ok) return;
    setError(null);
    try {
      await api.admin.deleteSchedule(s.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    }
  };

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {rows?.length === 0 ? (
        <section className="card">
          <p className="empty">{c.empty}</p>
        </section>
      ) : null}
      {rows && rows.length > 0 ? (
        <div className="table-region" role="region" aria-label={c.title} tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th>{c.columns.name}</th>
                <th>{c.columns.period}</th>
                <th>{c.columns.status}</th>
                <th>{c.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/schedules/${s.id}`} state={FROM_ADMIN}>
                      <strong>{s.name}</strong>
                    </Link>{' '}
                    {s.id === currentId ? <span className="badge physician">{copy.history.current}</span> : null}
                  </td>
                  <td>{formatPeriod(s.startDate, s.endDate)}</td>
                  <td>{s.status === 'draft' ? <span className="badge pending">{copy.schedule.draftBadge}</span> : <span className="badge">{copy.schedule.savedBadge}</span>}</td>
                  <td>
                    <div className="row">
                      <Link className="btn small" to={`/schedules/${s.id}`} state={FROM_ADMIN}>
                        {c.open}
                      </Link>
                      <button type="button" className="btn small danger" onClick={() => void remove(s)}>
                        {s.status === 'draft' ? c.discard : c.delete}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
